import { Button, Title1, makeStyles, tokens } from '@fluentui/react-components';
import { AuthProvider, useAuth, VersionBanner } from '@worklogix/web-common';

import { TenantShell } from './TenantShell';

const useStyles = makeStyles({
  signIn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingVerticalL,
    minHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
  },
});

/**
 * Sign-in prompt shown when no account is present. Lives inside the MSAL
 * provider (so {@link useAuth} works) but outside the authenticated shell.
 */
function SignInPrompt(): JSX.Element {
  const styles = useStyles();
  const { login } = useAuth();

  return (
    <main className={styles.signIn}>
      <Title1>WorkLogix</Title1>
      <p>Please sign in to continue.</p>
      <Button appearance="primary" onClick={() => void login()}>
        Sign in
      </Button>
    </main>
  );
}

/**
 * Auth gate: once the MSAL context is established, renders the authenticated
 * tenant shell for signed-in users and the sign-in prompt otherwise. Must live
 * inside {@link AuthProvider} so {@link useAuth} has an MSAL context.
 *
 * This tenant site NEVER probes `/platform/*`; tenant identity is derived
 * server-side from the verified token, so there is no client-side gate here.
 */
function AuthGate(): JSX.Element {
  const { isAuthenticated } = useAuth();

  return isAuthenticated ? <TenantShell /> : <SignInPrompt />;
}

/**
 * Application root for the tenant WFM site. Establishes the MSAL auth context,
 * keeps the version-check banner visible in every state, and hands off to the
 * auth gate. No platform-console gate and no `/platform/*` calls — this is the
 * tenant surface only.
 */
export function TenantApp(): JSX.Element {
  return (
    <AuthProvider>
      <VersionBanner />
      <AuthGate />
    </AuthProvider>
  );
}
